"use client";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";

// Irregular paint-splatter shapes (100x100 viewBox), hand-tuned for rough edges & satellites
const splatterShapes = [
  // 0 — wobbly amoeba
  "M50,6 C70,2 90,16 91,36 C99,52 88,76 66,82 C46,92 20,86 12,66 C2,50 10,26 28,16 C34,8 42,10 50,6 Z M14,72 C18,72 20,76 16,80 C12,82 10,76 14,72 Z",
  // 1 — splatter with side dribble
  "M42,18 C60,10 82,22 84,42 C92,60 72,80 48,78 C26,82 8,68 14,46 C4,38 18,16 32,20 C36,12 38,16 42,18 Z M86,64 C92,66 96,72 90,76 C82,78 80,68 86,64 Z M16,84 C18,82 22,86 20,90 C14,90 12,86 16,84 Z",
  // 2 — drippy spot with droplets
  "M30,28 C52,18 78,28 78,52 C84,70 62,82 44,80 C24,84 10,68 16,48 C12,38 20,28 30,28 Z M52,86 C54,94 48,98 44,92 C40,86 48,82 52,86 Z M82,18 C86,16 90,20 86,24 C82,26 78,20 82,18 Z",
  // 3 — stretched horizontal blob
  "M10,44 C30,28 64,30 84,42 C96,46 92,62 78,66 C56,74 26,72 14,62 C2,56 0,48 10,44 Z",
  // 4 — small splat with scattered satellites
  "M40,42 C58,32 72,42 74,54 C78,68 60,76 48,70 C32,76 24,62 30,50 C28,42 36,40 40,42 Z M18,28 C22,26 26,30 22,34 C18,34 14,30 18,28 Z M82,78 C86,76 92,80 86,84 C80,86 76,80 82,78 Z M14,82 C18,80 24,82 20,86 C14,88 10,84 14,82 Z M68,16 C72,14 76,18 70,22 C66,22 64,18 68,16 Z",
  // 5 — irregular wobble
  "M52,10 C74,6 90,24 86,46 C96,64 80,86 56,86 C32,92 12,76 12,54 C6,34 24,14 40,14 C44,8 48,8 52,10 Z M88,72 C92,72 94,78 88,80 C84,80 82,74 88,72 Z",
  // 6 — wide splat with arms
  "M22,38 C46,24 78,30 86,52 C94,68 76,82 56,80 C36,86 14,74 14,56 C8,48 14,40 22,38 Z M90,30 C94,30 96,34 92,36 C88,36 86,32 90,30 Z M28,82 C32,82 34,86 30,88 C24,88 22,84 28,82 Z",
  // 7 — small irregular dot cluster
  "M44,40 C58,34 70,44 68,56 C72,68 58,74 48,68 C36,72 30,60 36,52 C34,44 40,40 44,40 Z M22,22 C26,20 30,24 26,28 C22,28 18,24 22,22 Z M76,74 C80,72 84,76 80,80 C74,80 72,76 76,74 Z",
];

// Deterministic pseudo-random (Mulberry32) for stable SSR + dense splatter coverage
function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let x = Math.imul(t ^ (t >>> 15), 1 | t);
    x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

const splatters = (() => {
  const rand = mulberry32(20260515);
  const items: {
    x: string; y: string; size: number; shape: number; rotate: number;
    color: "navy" | "white"; opacity: number;
  }[] = [];
  // Very dense scatter of tiny splatters across the whole canvas
  for (let i = 0; i < 320; i++) {
    const r = rand();
    const tiny = r < 0.85;            // 85% extra-small
    const size = tiny
      ? 2 + Math.floor(rand() * 4)    // 2–5px
      : 5 + Math.floor(rand() * 4);   // 5–8px (cap)
    const isWhite = rand() < 0.8;     // 80% white-leaning
    items.push({
      x: `${(rand() * 100).toFixed(2)}%`,
      y: `${(rand() * 100).toFixed(2)}%`,
      size,
      shape: Math.floor(rand() * 8),
      rotate: Math.floor(rand() * 360),
      color: isWhite ? "white" : "navy",
      opacity: isWhite ? 0.6 + rand() * 0.4 : 0.35 + rand() * 0.35,
    });
  }
  return items;
})();

// Decorative yellow sparkles near the cutout
const sparkles = [
  { x: "76%", y: "8%", size: 26, rotate: 0 },
  { x: "92%", y: "18%", size: 18, rotate: 25 },
  { x: "84%", y: "32%", size: 14, rotate: -15 },
];

export default function Hero() {
  return (
    <section className="relative min-h-[90vh] flex flex-col justify-center overflow-hidden bg-primary">
      {/* Paint sprinkles / splatter background */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        {splatters.map((s, i) => (
          <svg
            key={i}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              left: s.x,
              top: s.y,
              width: s.size,
              height: s.size,
              transform: `translate(-50%, -50%) rotate(${s.rotate}deg)`,
              opacity: s.opacity,
              overflow: "visible",
            }}
          >
            <path d={splatterShapes[s.shape]} fill={s.color === "navy" ? "var(--foreground)" : "#ffffff"} />
          </svg>
        ))}
      </div>

      {/* Desktop: right column absolutely positioned over the text */}
      <motion.div
        className="hidden md:flex absolute md:right-[-10%] lg:right-[-50px] top-0 h-full w-[58%] lg:w-[60%] z-10 flex-col items-center justify-center pt-20"
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <Link
          href="/register"
          className="relative z-20 pointer-events-auto inline-flex items-center gap-2 bg-journey-1 text-foreground font-bold text-base lg:text-xl rounded-full px-8 lg:px-12 py-3 lg:py-4 shadow-md hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 active:scale-95 transition-all duration-150 mt-[102px]"
        >
          Register Now →
        </Link>
        <div className="relative w-full mt-10 lg:mt-14 pointer-events-none">
          {sparkles.map((s, i) => (
            <svg key={i} aria-hidden="true" style={{ position: "absolute", left: s.x, top: s.y, width: s.size, height: s.size, transform: `rotate(${s.rotate}deg)`, pointerEvents: "none" }} viewBox="0 0 24 24">
              <path d="M12,2 L14,10 L22,12 L14,14 L12,22 L10,14 L2,12 L10,10 Z" fill="var(--color-journey-1)" opacity="0.9" />
            </svg>
          ))}
          <Image src="/images/landing/hero-students-cutout.png" alt="Students" width={1280} height={720} priority className="w-full h-auto object-contain object-bottom drop-shadow-[0_18px_24px_rgba(34,64,77,0.25)]" />
        </div>
      </motion.div>

      <div className="container mx-auto px-4 relative z-20 pt-20 md:pt-28 pb-0 pointer-events-none">
        {/* Text */}
        <motion.div
          className="relative text-center md:text-left max-w-xl pointer-events-auto"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <p className="text-foreground text-base font-semibold tracking-widest uppercase mb-4">
            Your future starts here
          </p>
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[1.05] mb-6 whitespace-nowrap">
            <span className="block text-white">Your Money</span>
            <span className="block text-journey-1">Your Future</span>
            <span className="block text-foreground">Your Move</span>
          </h1>
          <p className="text-foreground text-lg md:text-xl leading-relaxed max-w-md mx-auto md:mx-0 mb-8">
            A free financial literacy program designed for students throughout the MENA region.
          </p>
          {/* Mobile: button below description */}
          <Link
            href="/register"
            className="md:hidden inline-flex items-center gap-2 bg-journey-1 text-foreground font-bold text-base rounded-full px-8 py-3 shadow-md hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 active:scale-95 transition-all duration-150"
          >
            Register Now →
          </Link>
        </motion.div>

        {/* Mobile: image below text */}
        <motion.div
          className="md:hidden mt-6 -mx-4"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <Image src="/images/landing/hero-students-cutout.png" alt="Students" width={1280} height={720} priority className="w-full h-auto object-contain object-bottom drop-shadow-[0_18px_24px_rgba(34,64,77,0.25)]" />
        </motion.div>
      </div>
    </section>
  );
}
